import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";
import { USER_APPLICATION_SESSION_KEY } from "../utils/globalUtility";

/**
 * Custom hook to block navigation attempts.
 */
function useBlocker(blocker, when = true) {
    const { navigator } = useContext(NavigationContext);

    useEffect(() => {
        if (!when || !navigator.block) return;

        const unblock = navigator.block((tx) => {
            const autoUnblockingTx = {
                ...tx,
                retry() {
                    // Automatically unblock the transition so it can play all the way
                    // through before retrying it. TODO: Figure out how to re-enable
                    // this block if the transition is cancelled for some reason.
                    unblock();
                    tx.retry();
                },
            };

            blocker(autoUnblockingTx);
        });

        return unblock;
    }, [navigator, blocker, when]);
}

/**
 * Custom hook to prompt user before navigation
 */
function usePrompt(message, when = true) {
  const blocker = useCallback(
    (tx) => {
      if (window.confirm(message)) {
        sessionStorage.removeItem(USER_APPLICATION_SESSION_KEY);
        tx.retry(); // Proceed with navigation
      }
    },
    [message]
  );

  // Trigger blocker only when `when` is true
  useBlocker(blocker, when);
}

// Create a context to manage unsaved changes
const UnsavedChangesContext = createContext();

/**
 * UnsavedChangesProvider: Manages unsaved changes state using a Set of dirty step names.
 * This allows per-step tracking so reverting one step doesn't clear changes from another.
 */
export const UnsavedChangesProvider = ({ isLoggedIn, children }) => {
  const [dirtySteps, setDirtySteps] = useState(() => new Set());
  const dirtyRef = useRef(dirtySteps);

  const hasUnsavedChanges = dirtySteps.size > 0;

  const addDirty = useCallback((key) => {
    setDirtySteps((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      dirtyRef.current = next;
      return next;
    });
  }, []);

  const removeDirty = useCallback((key) => {
    setDirtySteps((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      dirtyRef.current = next;
      return next;
    });
  }, []);

  const clearDirty = useCallback(() => {
    setDirtySteps((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set();
      dirtyRef.current = next;
      return next;
    });
  }, []);

  // Add a beforeunload listener for tab closing or page refresh.
  // Uses dirtyRef to avoid stale closure — clearDirty() updates the ref synchronously,
  // so even if beforeunload fires before React re-renders, it reads the latest value.
  useEffect(() => {
    const handleBeforeUnload = (event) => {
        if (dirtyRef.current.size > 0 && isLoggedIn) {
        event.preventDefault();
        event.returnValue = ""; // Show confirmation dialog
      } 
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isLoggedIn]);

  return (
    <UnsavedChangesContext.Provider value={{ hasUnsavedChanges, addDirty, removeDirty, clearDirty }}>
      {children}
    </UnsavedChangesContext.Provider>
  );
};

/**
 * Hook to access unsaved changes context
 */
export const useUnsavedChanges = () => useContext(UnsavedChangesContext);
